import React, { useState, useEffect } from 'react'; // Importing useState and useEffect hooks
import Header from '../Header';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx'; // Import XLSX
import { Modal, Button, Spinner } from 'react-bootstrap'; 

const EmployeeList = () => {
  const [employees, setEmployee] = useState([]);  // Default to an empty array
  const [loading, setLoading] = useState(true); // To track loading state
  const [error, setError] = useState(null); // To track error
  const [successMessage, setSuccessMessage] = useState(null); // To track success message
  const [managers, setManagers] = useState([]); // To store managers list
  const [showTransferModal, setShowTransferModal] = useState(false); // State to control modal visibility
  const [selectedEmployee, setSelectedEmployee] = useState(null); // Store the employee to be transferred
  const [selectedManager, setSelectedManager] = useState(null); // Store selected manager for transfer
  const [isTransferring, setIsTransferring] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Define the URL of the API
    const fetchData = async (employeeId) => {
      try {
        const response = await fetch('https://apiaksharaconnect.aksharafinalytics.in/employees');
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();  
        setEmployee(result.employee || []);  
      } catch (error) {
        console.error("Error fetching data:", error); 
        setError(error.message); 
      } finally {
        setLoading(false); 
      }
    };

    fetchData(); // Call the async function
  }, []);

  const handleAction = (action, id) => {
    if (action === 'edit') {
      navigate(`/Employee/edit/${id}`);
    }
    if (action === 'delete') {
      const confirmDelete = window.confirm("Are you sure you want to delete this manager?");
      if (confirmDelete) {
        deleteEmployee(id);  // Call delete function when action is delete
      }
    }
    if (action === 'transfer') {
      fetchManagers(id);  // Fetch managers for the employee to be transferred
      setSelectedEmployee(id);  // Store the selected employee id
      setShowTransferModal(true); // Show the transfer modal
    }
  };

  const deleteEmployee = async (id) => {
    try {
      const response = await fetch(`https://apiaksharaconnect.aksharafinalytics.in/employee_del/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),  // Pass the employee id in the request body
      });

      if (!response.ok) {
        throw new Error('Failed to delete employee');
      }
      // Remove the deleted employee from the employees list
      setEmployee((prevEmployees) => prevEmployees.filter((employee) => employee.id !== id));
      // Set success message
      setSuccessMessage('Employee deleted successfully!');
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("Error deleting employee:", error);
      setError('Error deleting employee');  // Handle errors
      setSuccessMessage(null); // Clear any previous success messages
    }
  };

  const fetchManagers = async (employeeId) => {
    try {
      const response = await fetch(`https://apiaksharaconnect.aksharafinalytics.in/mlist/${employeeId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch managers');
      }
      const result = await response.json();
      setManagers(result.data || []); // Store the list of managers
    } catch (error) {
      console.error("Error fetching managers:", error);
      setError('Error fetching managers');
    }
  };

  const transferEmployee = async () => {
    if (!selectedManager) {
      alert('Please select a manager');
      return;
    }
    setIsTransferring(true); // Start spinner
    try {
      const response = await fetch(`https://apiaksharaconnect.aksharafinalytics.in/addmgr/${selectedEmployee}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mgr_id: selectedManager }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to transfer employee');
      }
  
      setShowTransferModal(false);
      setSuccessMessage('Employee transferred successfully!');
      setError(null);
    } catch (error) {
      setError('Error transferring employee');
      setSuccessMessage(null);
    } finally {
      setIsTransferring(false); // Stop spinner
    }
  };
  

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(employees);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "employees");

    XLSX.writeFile(wb, "Employee_List.xlsx");
  };

  if (loading) {
    return <div className="text-center mt-5">
            <Spinner animation="border" variant="primary" />
          </div>;
  }

  return (
    <>
      <Header />
      <div className="shadow mx-1 px-3 bg-light rounded-3">
        <div className="d-flex align-items-center justify-content-between p-3 mt-3">
          <div className="d-flex">
            <h4 className="mb-0 me-3">Employee Management</h4>
            <button className="btn btn-primary" onClick={() => navigate('/Employee')}>
              Add Employee
            </button>
          </div>
          <div>
            <button className="btn btn-success" onClick={exportToExcel}>
              Export to Excel
            </button>
          </div>
        </div>
        {/* Show error or success messages */}
        {error && <div className="alert alert-danger">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}
        <div className="card">
          <div className="card-header">Employee List</div>
          <div className="card-body">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>S.no</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees?.length > 0 ? (
                  employees.map((employee, index) => (
                    <tr key={employee.id}>
                      <td>{index + 1}</td>
                      <td> <a href={`/EmpInfo/${employee.id}`} className="text-decoration-none">
                          {employee.name || 'N/A'}
                        </a></td>
                      <td>{employee.email || 'N/A'}</td>
                      <td>{employee.phone || 'N/A'}</td>
                      <td>
                        <button
                          className="btn btn-warning"
                          onClick={() => handleAction('edit', employee.id)}
                        >
                          Edit
                        </button>
                        <button className="btn btn-danger ms-2" onClick={() => handleAction('delete', employee.id)}>Delete</button>
                        <button className="btn btn-info ms-2" onClick={() => handleAction('transfer', employee.id)}>Transfer</button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">No employees available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transfer Modal */}
        <Modal show={showTransferModal} onHide={() => setShowTransferModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Transfer Employee</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label htmlFor="managerSelect">Select Manager</label>
              <select
                id="managerSelect"
                className="form-control"
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
              >
                <option value="">Select Manager</option>
                {managers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTransferModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={transferEmployee} disabled={isTransferring}>
              {isTransferring ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Transferring...
                </>
              ) : (
                "Transfer"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default EmployeeList;
